'use client';

import { useCallback, useState } from 'react';

export interface AuthFormErrors {
  email?: string;
  password?: string;
  name?: string;
  confirmPassword?: string;
  general?: string;
}

/**
 * Lightweight auth-form validation state used by the sign-in / sign-up forms.
 * The actual login/register calls go through useAuth; this only holds field
 * errors and the shared validators.
 */
export const useAuthForm = () => {
  const [errors, setErrors] = useState<AuthFormErrors>({});
  const [loading, setLoading] = useState(false);

  const validateEmail = useCallback((email: string): boolean => /.+@.+\..+/.test(email), []);

  const clearErrors = useCallback(() => setErrors({}), []);

  const validateLoginForm = useCallback((data: { email: string; password: string }): boolean => {
    const next: AuthFormErrors = {};
    if (!validateEmail(data.email)) next.email = 'Enter a valid email address';
    if (!data.password) next.password = 'Password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [validateEmail]);

  const validateRegisterForm = useCallback(
    (data: { name?: string; email: string; password: string; confirmPassword?: string }): boolean => {
      const next: AuthFormErrors = {};
      if (!data.name) next.name = 'Name is required';
      if (!validateEmail(data.email)) next.email = 'Enter a valid email address';
      if ((data.password ?? '').length < 10) next.password = 'Password must be at least 10 characters';
      if (data.confirmPassword !== undefined && data.confirmPassword !== data.password)
        next.confirmPassword = 'Passwords do not match';
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [validateEmail],
  );

  return { errors, loading, setLoading, clearErrors, validateEmail, validateLoginForm, validateRegisterForm };
};
